import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Minus, PanelLeft, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { acquirePdf, loadPdfJs, pdfPageAspect, thumbnailWidth } from '@/lib/pdfjs';
import { DEFAULT_PAGE_ASPECT } from '@/lib/pdfStack';
import { PdfThumbnail } from './PdfThumbnail';
import { PdfViewer } from './PdfViewer';
import './pdfReader.css';

// Full-screen PDF reader: page rail on the left, continuous pages rendered
// with pdf.js (selectable text, so copy and Cmd+F work), page jump, zoom
// (buttons, keys, Ctrl/Cmd+wheel and trackpad pinch). Falls back to the
// browser's own viewer when pdf.js can't open the file.

const ZOOM_STEPS = [0.5, 0.67, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];
const MIN_ZOOM = ZOOM_STEPS[0];
const MAX_ZOOM = ZOOM_STEPS[ZOOM_STEPS.length - 1];
const PAGE_GAP = 20;
// Cap the backing-store density, and the canvas size (iOS refuses canvases over ~16.7M pixels)
const MAX_PIXEL_RATIO = 2;
const MAX_CANVAS_PIXELS = 16_000_000;
const RAIL_THUMB_WIDTH = 112;

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

interface PdfReaderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
  subtitle?: string;
  initialPage?: number;
  onPageChange?: (page: number) => void;
}

/** Keeps reader input away from the canvas behind it (React portals bubble to the card). */
const isolate = (e: React.SyntheticEvent) => e.stopPropagation();

export function PdfReaderDialog({ open, onOpenChange, url, title, subtitle, initialPage, onPageChange }: PdfReaderDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/75 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-50 inset-0 sm:inset-3 lg:inset-x-8 lg:inset-y-5 flex flex-col overflow-hidden sm:rounded-2xl',
            'bg-background shadow-2xl outline-none sm:ring-1 sm:ring-white/10',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98] duration-200',
          )}
          onMouseDown={isolate}
          onPointerDown={isolate}
          onTouchStart={isolate}
          onClick={isolate}
          onDoubleClick={isolate}
          onContextMenu={isolate}
          onWheel={isolate}
          onKeyDown={isolate}
          onOpenAutoFocus={(e) => {
            // Focus the pages, not the first toolbar button, so arrow keys and space scroll them
            e.preventDefault();
            (e.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-pdf-scroll]')?.focus({ preventScroll: true });
          }}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          <PdfReader
            url={url}
            title={title}
            subtitle={subtitle}
            initialPage={initialPage}
            onPageChange={onPageChange}
            onClose={() => onOpenChange(false)}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface PdfReaderProps {
  url: string;
  title: string;
  subtitle?: string;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  onClose?: () => void;
}

export function PdfReader({ url, title, subtitle, initialPage = 1, onPageChange, onClose }: PdfReaderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lastWidthRef = useRef(0);
  const anchorRef = useRef({ page: 1, frac: 0 });
  const jumpedRef = useRef(false);
  const onPageChangeRef = useRef(onPageChange);

  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState(false);
  const [firstAspect, setFirstAspect] = useState(DEFAULT_PAGE_ASPECT);
  const [aspects, setAspects] = useState<Record<number, number>>({});
  const [viewportW, setViewportW] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [page, setPage] = useState(initialPage);
  const [pageInput, setPageInput] = useState<string | null>(null);
  const [railOpen, setRailOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 900);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  useEffect(() => {
    let cancelled = false;
    const { promise, release } = acquirePdf(url);
    promise
      .then(async (pdf) => {
        const aspect = await pdfPageAspect(pdf, 1);
        if (cancelled) return;
        setFirstAspect(aspect);
        setDoc(pdf);
      })
      .catch((err) => {
        console.error('[PdfReader] Failed to load PDF', err);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      release();
    };
  }, [url]);

  // Width only: page heights follow the width, so reacting to height would loop
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const apply = (w: number) => {
      if (Math.abs(w - lastWidthRef.current) > 1) {
        lastWidthRef.current = w;
        setViewportW(w);
      }
    };
    apply(el.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => apply(w), 100);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [error]);

  const numPages = doc?.numPages ?? 0;
  const narrow = viewportW > 0 && viewportW < 640;
  const gutter = narrow ? 12 : 40;
  // Fit to width, capped so a portrait page stays a readable column on a wide screen
  const fitWidth = Math.max(0, Math.min(viewportW - gutter * 2, firstAspect < 1 ? 1280 : 880));
  const pageWidth = Math.round(fitWidth * zoom);
  const heightOf = (n: number) => Math.round(pageWidth * (aspects[n] ?? firstAspect));

  const handleAspect = useCallback((n: number, aspect: number) => {
    setAspects((prev) => (Math.abs((prev[n] ?? 0) - aspect) < 0.001 ? prev : { ...prev, [n]: aspect }));
  }, []);

  // Which page sits under the top third of the view, and where the top edge is inside its page
  const syncFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const pages = pageRefs.current;
    const top = el.scrollTop;
    const probe = top + el.clientHeight * 0.35;
    let current = 1;
    let anchor = { page: 1, frac: 0 };
    for (let i = 0; i < pages.length; i++) {
      const p = pages[i];
      if (!p) continue;
      if (p.offsetTop <= probe) current = i + 1;
      if (p.offsetTop <= top + 1) anchor = { page: i + 1, frac: (top - p.offsetTop) / Math.max(1, p.offsetHeight) };
    }
    anchorRef.current = anchor;
    setPage(current);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        syncFromScroll();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [syncFromScroll, error]);

  const goToPage = useCallback((n: number, smooth = true) => {
    const el = scrollRef.current;
    const target = pageRefs.current[n - 1];
    if (!el || !target) return;
    el.scrollTo({ top: target.offsetTop - PAGE_GAP, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // First layout: open on the page the card was showing. After that, zooming
  // or resizing keeps the same spot of the same page at the top.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !doc || pageWidth === 0) return;
    if (!jumpedRef.current) {
      jumpedRef.current = true;
      if (initialPage > 1) goToPage(Math.min(initialPage, doc.numPages), false);
      return;
    }
    const { page: p, frac } = anchorRef.current;
    const target = pageRefs.current[p - 1];
    if (!target) return;
    el.scrollTop = target.offsetTop + frac * target.offsetHeight;
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    // initialPage and goToPage only matter for the first jump
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, pageWidth]);

  useEffect(() => {
    onPageChangeRef.current?.(page);
  }, [page]);

  // Keep the active thumbnail in view
  useEffect(() => {
    const rail = railRef.current;
    const thumb = rail?.querySelector<HTMLElement>(`[data-thumb="${page}"]`);
    if (!rail || !thumb) return;
    const top = thumb.offsetTop - rail.offsetTop;
    if (top < rail.scrollTop + 8 || top + thumb.offsetHeight > rail.scrollTop + rail.clientHeight - 8) {
      rail.scrollTo({ top: top - rail.clientHeight / 2 + thumb.offsetHeight / 2, behavior: 'smooth' });
    }
  }, [page, railOpen]);

  const stepZoom = useCallback((dir: 1 | -1) => {
    setZoom((z) => {
      const next = dir > 0 ? ZOOM_STEPS.find((s) => s > z + 0.001) : [...ZOOM_STEPS].reverse().find((s) => s < z - 0.001);
      return next ?? z;
    });
  }, []);

  // Ctrl/Cmd+wheel and trackpad pinch zoom the pages, not the browser or the canvas
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setZoom((z) => clampZoom(z * Math.exp(-e.deltaY * 0.01)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [error]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    const mod = e.metaKey || e.ctrlKey;
    if (e.key === 'ArrowRight' && !mod) {
      e.preventDefault();
      goToPage(Math.min(numPages, page + 1));
    } else if (e.key === 'ArrowLeft' && !mod) {
      e.preventDefault();
      goToPage(Math.max(1, page - 1));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      stepZoom(1);
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault();
      stepZoom(-1);
    } else if (e.key === '0') {
      e.preventDefault();
      setZoom(1);
    } else if (e.key === 'Home' && !mod) {
      e.preventDefault();
      goToPage(1, false);
    } else if (e.key === 'End' && !mod) {
      e.preventDefault();
      goToPage(numPages, false);
    }
  };

  const commitPageInput = () => {
    const n = parseInt(pageInput ?? '', 10);
    setPageInput(null);
    if (Number.isFinite(n) && numPages > 0) goToPage(Math.min(numPages, Math.max(1, n)));
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Cross-origin links ignore <a download>, so fetch it and save the blob under its own name
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = /\.pdf$/i.test(title) ? title : `${title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  const iconButton = 'inline-flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none';
  const showRail = railOpen && !narrow && !error;

  return (
    <div className="flex flex-col h-full min-h-0" onKeyDown={handleKeyDown}>
      {/* Toolbar */}
      <div className="relative z-10 flex items-center gap-2 h-14 px-2 sm:px-3 border-b border-border/60 bg-background/95 backdrop-blur shrink-0">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {!error && (
            <button
              type="button"
              className={cn(iconButton, 'hidden sm:inline-flex', railOpen && 'text-foreground bg-muted')}
              onClick={() => setRailOpen((o) => !o)}
              aria-label={railOpen ? 'Hide pages' : 'Show pages'}
              aria-pressed={railOpen}
              title={railOpen ? 'Hide pages' : 'Show pages'}
            >
              <PanelLeft className="w-4 h-4" />
            </button>
          )}
          <span className="shrink-0 ml-1 rounded-[4px] bg-red-500 text-white text-[9px] font-bold leading-none px-1.5 py-1 tracking-wider">PDF</span>
          <div className="min-w-0 ml-1.5">
            <div className="text-sm font-medium truncate" data-testid="pdf-reader-title">{title}</div>
            {(subtitle || numPages > 0) && (
              <div className="text-[11px] text-muted-foreground truncate hidden sm:block">
                {[numPages > 0 ? `${numPages} ${numPages === 1 ? 'page' : 'pages'}` : null, subtitle || null].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>

        {/* Page jump */}
        {numPages > 0 && !error && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button type="button" className={cn(iconButton, 'hidden sm:inline-flex')} onClick={() => goToPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 text-sm tabular-nums">
              <input
                className="w-10 h-8 rounded-md border border-border bg-muted/40 text-center text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={pageInput ?? String(page)}
                inputMode="numeric"
                aria-label="Page number"
                onFocus={(e) => { setPageInput(String(page)); e.currentTarget.select(); }}
                onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={commitPageInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { commitPageInput(); (e.target as HTMLInputElement).blur(); }
                  if (e.key === 'Escape') { e.stopPropagation(); setPageInput(null); (e.target as HTMLInputElement).blur(); }
                }}
              />
              <span className="text-muted-foreground" data-testid="pdf-reader-total">/ {numPages}</span>
            </div>
            <button type="button" className={cn(iconButton, 'hidden sm:inline-flex')} onClick={() => goToPage(page + 1)} disabled={page >= numPages} aria-label="Next page">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-end gap-0.5 flex-1 min-w-0">
          {!error && (
            <div className="hidden md:flex items-center gap-0.5 mr-1 rounded-lg bg-muted/50 p-0.5">
              <button type="button" className={cn(iconButton, 'h-7 w-7')} onClick={() => stepZoom(-1)} disabled={zoom <= MIN_ZOOM + 0.001} aria-label="Zoom out" title="Zoom out (−)">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                className="h-7 min-w-[52px] px-1.5 rounded-md text-xs tabular-nums text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                onClick={() => setZoom(1)}
                title="Fit to width (0)"
                data-testid="pdf-reader-zoom"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" className={cn(iconButton, 'h-7 w-7')} onClick={() => stepZoom(1)} disabled={zoom >= MAX_ZOOM - 0.001} aria-label="Zoom in" title="Zoom in (+)">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button type="button" className={cn(iconButton, 'hidden sm:inline-flex')} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} aria-label="Open in new tab" title="Open in new tab">
            <ExternalLink className="w-4 h-4" />
          </button>
          <button type="button" className={iconButton} onClick={handleDownload} disabled={downloading} aria-label="Download" title="Download">
            <Download className="w-4 h-4" />
          </button>
          {onClose && (
            <button type="button" className={cn(iconButton, 'ml-1')} onClick={onClose} aria-label="Close" title="Close (Esc)">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Page rail */}
        {showRail && (
          <div
            ref={railRef}
            className="w-[152px] shrink-0 overflow-y-auto overscroll-contain border-r border-border/60 bg-muted/30 py-3"
            data-testid="pdf-reader-rail"
          >
            {doc
              ? Array.from({ length: numPages }, (_, i) => (
                  <RailThumb
                    key={i}
                    url={url}
                    doc={doc}
                    pageNumber={i + 1}
                    aspect={aspects[i + 1] ?? firstAspect}
                    active={page === i + 1}
                    root={railRef.current}
                    onSelect={() => goToPage(i + 1, false)}
                  />
                ))
              : Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="mx-auto mb-4 rounded-sm bg-background animate-pulse" style={{ width: RAIL_THUMB_WIDTH, height: RAIL_THUMB_WIDTH * firstAspect }} />
                ))}
          </div>
        )}

        {/* Pages */}
        {error ? (
          <div className="flex-1 min-w-0 relative bg-muted/40">
            <PdfViewer url={url} title={title} onDownload={() => window.open(url, '_blank', 'noopener,noreferrer')} />
          </div>
        ) : (
          <div
            ref={scrollRef}
            data-pdf-scroll
            tabIndex={-1}
            className="pdf-reader-pages relative flex-1 min-w-0 overflow-auto overscroll-contain outline-none bg-neutral-100 dark:bg-neutral-900"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y', scrollbarGutter: 'stable' }}
            data-testid="pdf-reader-pages"
          >
            {pageWidth > 0 && (
              <div className="flex flex-col items-center mx-auto" style={{ gap: PAGE_GAP, padding: `${PAGE_GAP}px ${gutter}px`, width: 'max-content', minWidth: '100%' }}>
                {doc
                  ? Array.from({ length: numPages }, (_, i) => (
                      <ReaderPage
                        key={i}
                        ref={(el) => { pageRefs.current[i] = el; }}
                        doc={doc}
                        pageNumber={i + 1}
                        width={pageWidth}
                        height={heightOf(i + 1)}
                        root={scrollRef.current}
                        onAspect={handleAspect}
                      />
                    ))
                  : Array.from({ length: 2 }, (_, i) => (
                      <div key={i} className="bg-white rounded-sm shadow-sm animate-pulse" style={{ width: pageWidth, height: pageWidth * firstAspect }} />
                    ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Rail thumbnail ──────────────────────────────────────────────────────

interface RailThumbProps {
  url: string;
  doc: PDFDocumentProxy;
  pageNumber: number;
  aspect: number;
  active: boolean;
  root: HTMLElement | null;
  onSelect: () => void;
}

function RailThumb({ url, doc, pageNumber, aspect, active, root, onSelect }: RailThumbProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [seen, setSeen] = useState(pageNumber <= 6);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setSeen(true); },
      { root, rootMargin: '300px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [root, seen]);

  return (
    <button
      ref={ref}
      type="button"
      data-thumb={pageNumber}
      onClick={onSelect}
      className="group/thumb flex flex-col items-center gap-1.5 w-full pb-4 focus:outline-none"
      aria-label={`Go to page ${pageNumber}`}
      aria-current={active ? 'page' : undefined}
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-[3px] bg-white transition-shadow',
          active ? 'ring-2 ring-primary shadow-md' : 'ring-1 ring-black/10 shadow-sm group-hover/thumb:ring-black/25 group-focus-visible/thumb:ring-2 group-focus-visible/thumb:ring-ring',
        )}
        style={{ width: RAIL_THUMB_WIDTH, height: Math.round(RAIL_THUMB_WIDTH * aspect) }}
      >
        <PdfThumbnail url={url} doc={seen ? doc : null} pageNumber={pageNumber} renderWidth={thumbnailWidth(RAIL_THUMB_WIDTH)} />
      </div>
      <span className={cn('text-[11px] tabular-nums', active ? 'text-foreground font-medium' : 'text-muted-foreground')}>{pageNumber}</span>
    </button>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

interface ReaderPageProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  height: number;
  root: HTMLElement | null;
  onAspect: (pageNumber: number, aspect: number) => void;
}

const ReaderPage = React.forwardRef<HTMLDivElement, ReaderPageProps>(function ReaderPage(
  { doc, pageNumber, width, height, root, onAspect },
  forwardedRef,
) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [isNear, setIsNear] = useState(pageNumber <= 2);
  const [rendered, setRendered] = useState(false);

  const setRefs = useCallback((el: HTMLDivElement | null) => {
    wrapperRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  }, [forwardedRef]);

  // Only keep pages within a screen or so of the viewport rendered
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsNear(entry.isIntersecting),
      { root, rootMargin: '120% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [root]);

  useEffect(() => {
    const host = canvasHostRef.current;
    const textDiv = textRef.current;
    if (!host || !textDiv) return;

    if (!isNear) {
      // Free the bitmap and text; the wrapper keeps its height so scroll position holds
      host.replaceChildren();
      textDiv.replaceChildren();
      setRendered(false);
      return;
    }

    let cancelled = false;
    let task: RenderTask | null = null;
    let textLayer: { cancel: () => void } | null = null;

    // Wait for zoom gestures to settle; the old bitmap stretches in the meantime
    const timer = setTimeout(async () => {
      try {
        const page = await doc.getPage(pageNumber);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        onAspect(pageNumber, base.height / base.width);
        const scale = width / base.width;
        const viewport = page.getViewport({ scale });
        let ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
        const pixels = viewport.width * viewport.height * ratio * ratio;
        if (pixels > MAX_CANVAS_PIXELS) ratio *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);

        // Draw into a fresh canvas and swap it in when done, so zooming never flashes blank
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.cssText = 'width:100%;height:100%;display:block';
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        task = page.render({
          canvasContext: ctx,
          viewport,
          transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
        });
        await task.promise;
        if (cancelled) return;
        host.replaceChildren(canvas);
        setRendered(true);

        // Selectable text on top of the bitmap
        const pdfjs = await loadPdfJs();
        if (cancelled) return;
        textDiv.replaceChildren();
        textDiv.style.setProperty('--scale-factor', String(scale));
        const layer = new pdfjs.TextLayer({
          textContentSource: page.streamTextContent(),
          container: textDiv,
          viewport,
        });
        textLayer = layer;
        await layer.render();
      } catch (err) {
        const name = (err as { name?: string })?.name;
        if (name !== 'RenderingCancelledException' && name !== 'AbortException') {
          console.error('[PdfReader] Page render failed', err);
        }
      }
    }, rendered ? 140 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      task?.cancel();
      textLayer?.cancel();
    };
    // `rendered` only picks the delay; re-running on it would render twice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, pageNumber, width, isNear, onAspect]);

  return (
    <div
      ref={setRefs}
      className="relative bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_8px_24px_-12px_rgba(0,0,0,0.25)] shrink-0"
      style={{ width, height }}
      data-page={pageNumber}
    >
      <div ref={canvasHostRef} className="absolute inset-0" />
      {!rendered && <div className="absolute inset-0 animate-pulse bg-neutral-50" />}
      <div ref={textRef} className="textLayer" />
    </div>
  );
});
